import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ZoomOut, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PhotoLightboxProps {
  photos: string[];
  initialIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PhotoLightbox({ photos, initialIndex = 0, open, onOpenChange }: PhotoLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setZoom(1);
    }
  }, [open, initialIndex]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'Escape') onOpenChange(false);
    if (e.key === 'ArrowRight' && currentIndex < photos.length - 1) setCurrentIndex(prev => prev + 1);
    if (e.key === 'ArrowLeft' && currentIndex > 0) setCurrentIndex(prev => prev - 1);
  }, [open, currentIndex, photos.length, onOpenChange]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!open || photos.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
        onClick={() => onOpenChange(false)}
      >
        {/* Close button */}
        <button
          onClick={() => onOpenChange(false)}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Zoom controls */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          <button onClick={(e) => { e.stopPropagation(); setZoom(prev => Math.max(0.5, prev - 0.25)); }}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white">
            <ZoomOut className="h-5 w-5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setZoom(1); }}
            className="px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white text-sm">
            {Math.round(zoom * 100)}%
          </button>
          <button onClick={(e) => { e.stopPropagation(); setZoom(prev => Math.min(3, prev + 0.25)); }}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white">
            <ZoomIn className="h-5 w-5" />
          </button>
        </div>

        {/* Image */}
        <motion.img
          key={currentIndex}
          src={photos[currentIndex]}
          alt={`Photo ${currentIndex + 1}`}
          className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg select-none"
          style={{ transform: `scale(${zoom})`, transition: 'transform 0.2s ease' }}
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          draggable={false}
        />

        {/* Navigation dots */}
        {photos.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {photos.map((_, i) => (
              <button key={i} onClick={(e) => { e.stopPropagation(); setCurrentIndex(i); }}
                className={cn("w-2.5 h-2.5 rounded-full transition-colors", i === currentIndex ? "bg-white" : "bg-white/40 hover:bg-white/60")} />
            ))}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

// Small "Click to enlarge" affordance for photos
export function EnlargeablePhoto({
  src, alt, className, onClick,
}: {
  src: string;
  alt: string;
  className?: string;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className={cn("relative group cursor-pointer", className)}>
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        crossOrigin="anonymous"
        loading="eager"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          const decoded = decodeURIComponent(src);
          if (target.src !== decoded) {
            target.src = decoded;
          }
        }}
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
        <div className="bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
          <Search className="h-3 w-3" />
          <span>Click to enlarge</span>
        </div>
      </div>
    </button>
  );
}
